import React from "react";
import PropTypes from "prop-types";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import withStyles from "@material-ui/core/styles/withStyles";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import OSSTooltip from "../OSSTooltip/OSSTooltip";
import { useAnalyticsProvider } from "../index";
import HeaderCell from "./HeaderCell/HeaderCell";

const styles = theme => ({
  alignCenter: {
    display: "flex",
    alignItems: "center"
  },
  cellWithSubLabel: {
    display: "flex",
    flexDirection: "column"
  }
});

const OSSTableHead = ({
  activeColumnHandler,
  classes,
  columns,
  onRequestSort,
  query,
  sortDirectionHandler,
  TableHeadProps,
  componentContext
}) => {
  const setTrackerData = useAnalyticsProvider();

  /**
   * Determines if a column is active by checking ordering
   * property in the query against a given sort key.
   *
   * (i.e. is the table currently being sorted by
   * this key? If yes, then it's active)
   *
   * @param {string} sortByKey
   * @returns {boolean}
   */
  const isColumnActive = sortByKey => {
    const { ordering } = query;

    const isActive = ordering && ordering.replace("-", "") === sortByKey;

    return isActive;
  };

  /**
   * Returns the current sort direction based on
   * the ordering property in the query.
   *
   * @returns {string}
   */
  const getSortDirection = () => {
    const { ordering } = query;
    const DESC = "desc";
    const ASC = "asc";

    const sortDirection = ordering && ordering.charAt(0) === "-" ? DESC : ASC;

    return sortDirection;
  };

  return (
    <TableHead {...TableHeadProps}>
      <TableRow>
        {columns?.map((column, index) => {
          const {
            dataKey,
            header,
            label,
            subLabel,
            OSSTooltipProps,
            sorting,
            sortKey,
            TableCellProps,
            TableSortLabelProps,
            tooltip,
            width
          } = column || {};

          const sortByKey = sortKey ? sortKey : dataKey;
          const columnActive = activeColumnHandler
            ? activeColumnHandler(sortByKey)
            : isColumnActive(sortByKey);
          const sortDirection = sortDirectionHandler
            ? sortDirectionHandler()
            : getSortDirection();
          const cellLabel = label ? label : dataKey ? dataKey : "";

          const renderCellContents = () => (
            <span className={classes.alignCenter}>
              <OSSTooltip title={tooltip ? tooltip : ""} {...OSSTooltipProps}>
                {subLabel ? (
                  <div className={classes.cellWithSubLabel}>
                    {cellLabel}
                    {subLabel && <small>{subLabel}</small>}
                  </div>
                ) : (
                  <div>{cellLabel}</div>
                )}
              </OSSTooltip>
            </span>
          );

          return (
            <TableCell
              {...TableCellProps}
              key={index}
              style={{ width: width ? width : "auto" }}
            >
              {header ? (
                OSSTableHead.Cell(column)
              ) : sorting ? (
                <TableSortLabel
                  active={columnActive}
                  direction={sortDirection}
                  onClick={() => {
                    setTrackerData({
                      method: "event",
                      category: "Table Data",
                      action: "Sort",
                      label: `${cellLabel}: ${componentContext}`
                    });
                    onRequestSort(sortByKey);
                  }}
                  {...TableSortLabelProps}
                >
                  {renderCellContents()}
                </TableSortLabel>
              ) : (
                renderCellContents()
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

OSSTableHead.Cell = column => {
  const { header } = column;
  const validCell = React.isValidElement(header) ? header : <HeaderCell />;

  return React.cloneElement(validCell, { column });
};

OSSTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRequestSort: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  sortDirectionHandler: PropTypes.func,
  TableHeadProps: PropTypes.object
};

export default withStyles(styles)(OSSTableHead);
